<template>
    <div
        ref="root"
        :class="$style.root"
        :style="{ width: `${box.width}px`, height: `${box.height}px` }"
    >
        <div
            :id="id"
            :class="$style.slot"
        />
        <img
            v-if="isFallbackVisible"
            :class="$style.fallback"
            :src="fallbackImage"
            :alt="tr('adFreeBanners.noAds')"
        />
    </div>
</template>
<script lang="ts" setup>
import { useResizeObserver, useThrottleFn } from '@vueuse/core'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, useId, useTemplateRef, watch } from 'vue'

import { useTr } from '@/composables/use-tr'
import { getNativeBannerSlot } from '@/modules/adv/native-banners'
import type { PageAd } from '@/modules/adv/page-ad'
import type { PageAdType } from '@/modules/adv/types'
import { useAdDisabledStore } from '@/store/ad-disabled-store'
import { useAdsStore } from '@/store/ads-store'
import { useLayoutStore } from '@/store/layout-store'

import noAdsBanner from './images/no_ads2x.webp'
import { hasFallbackCreative, hasRenderedBox, isElementCovered, matchesFullscreenMode } from './native-banner-rules'

const props = defineProps<{ placement: PageAdType }>()

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
}>()

const COVERAGE_POLL_MS = 1000

const adsStore = useAdsStore()
const adDisabledStore = useAdDisabledStore()
const layoutStore = useLayoutStore()
const tr = useTr()

const id = useId()
const rootRef = useTemplateRef('root')
const banner = shallowRef<PageAd>()
const isCovered = ref(false)
const hasBox = ref(false)
const hasNoFill = ref(false)
const fallbackImage = noAdsBanner

const pollId = ref<number>()

const viewportWidth = ref(import.meta.env.SSR ? 0 : window.innerWidth)

const nativeSlot = computed(() => getNativeBannerSlot(props.placement, viewportWidth.value))

const box = computed(() => {
    const sizes = nativeSlot.value?.sizes ?? []
    return {
        width: Math.max(0, ...sizes.map(([width]) => width)),
        height: Math.max(0, ...sizes.map(([, height]) => height)),
    }
})

const isFallbackVisible = computed(() => hasNoFill.value && hasFallbackCreative(props.placement))

const matchesFullscreen = computed(() => matchesFullscreenMode(props.placement, layoutStore.fullScreen))

const canShowBanner = computed(
    () =>
        !!nativeSlot.value &&
        !!adsStore.adService &&
        !adDisabledStore.isAdDisabled &&
        matchesFullscreen.value &&
        hasBox.value &&
        !isCovered.value,
)

function measureBox() {
    const el = rootRef.value
    if (!el) {
        return
    }

    viewportWidth.value = window.innerWidth
    hasBox.value = hasRenderedBox(el)
    isCovered.value = hasBox.value && isElementCovered(el)

    if (banner.value && hasBox.value) {
        banner.value.updatePosition()
    }
}

const measureBoxThrottled = useThrottleFn(measureBox, 200)

function reportNoFill() {
    hasNoFill.value = true
    emit('update:visible', isFallbackVisible.value)
}

function requestBanner() {
    if (banner.value || !adsStore.adService || !nativeSlot.value) {
        return
    }

    hasNoFill.value = false

    const publicBanner = adsStore.adService.requestPageAd({
        placement: props.placement,
        el: id,
        sizes: nativeSlot.value.sizes,
    })

    publicBanner.addEventListener('rendered', () => {
        hasNoFill.value = false
        emit('update:visible', true)
    })

    publicBanner.addEventListener('native_no_fill', reportNoFill)
    publicBanner.addEventListener('empty', destroyBanner)
    publicBanner.addEventListener('closed', destroyBanner)
    publicBanner.addEventListener('destroyed', destroyBanner)
    publicBanner.addEventListener('failed', destroyBanner)

    banner.value = publicBanner
}

function destroyBanner() {
    if (!banner.value) {
        return
    }

    banner.value.destroy()
    banner.value = undefined
    hasNoFill.value = false
    emit('update:visible', false)
}

function syncBanner() {
    if (canShowBanner.value) {
        requestBanner()
    } else {
        destroyBanner()
    }
}

watch(canShowBanner, syncBanner)

// Entering fullscreen puts an overlay over the page without resizing anything.
watch(
    () => layoutStore.fullScreen,
    () => nextTick(measureBox),
)

useResizeObserver(rootRef, () => {
    measureBoxThrottled()
})

onMounted(() => {
    measureBox()
    syncBanner()
    pollId.value = window.setInterval(measureBox, COVERAGE_POLL_MS)
    window.addEventListener('resize', measureBoxThrottled, { passive: true })
})

onBeforeUnmount(() => {
    window.clearInterval(pollId.value)
    window.removeEventListener('resize', measureBoxThrottled)
    destroyBanner()
})
</script>
<style module>
.root {
    position: relative;
    max-width: 100%;
    /* Hit-testable on purpose: coverage detection relies on this box answering elementFromPoint. */
    pointer-events: auto;
}

.slot {
    width: 100%;
    height: 100%;
}

.fallback {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
}
</style>
